import { isPlainObject, map, snakeCase, toLower, camelCase } from 'lodash'
import resolveResponse from 'contentful-resolve-response'
import slugify from 'slugify'
import { CONTENTFUL_ASSETS_HOSTNAMES } from '../config'

// recursively converts the content model of a program from raw_mode. Handles arrays, objects, and individual entries such as strings
export function buildContentModel(rawContentModel) {
  if (Array.isArray(rawContentModel)) {
    return rawContentModel.filter((item) => item != null).map((item) => {
      if (item.fields !== undefined) {
        return {
          id: item.sys?.id,
          locale: item.sys?.locale,
          type: item.sys?.type,
          ...buildContentModel(item.fields),
        }
      } else {
        return buildContentModel(item)
      }
    })
  } else if (isPlainObject(rawContentModel)) {
    // Check if this is a Contentful Asset
    if (rawContentModel.sys?.type === 'Asset') {
      const assetObject = {...rawContentModel}
      // If the asset has a file URL, rewrite it
      if (assetObject.fields?.file?.url) {
        assetObject.fields = {
          ...assetObject.fields,
          file: {
            ...assetObject.fields.file,
            url: rewriteUrl(assetObject.fields.file.url)
          }
        }
      }
      return assetObject
    }
    
    // Check if this is a program or other content type with sys.contentType
    if (rawContentModel.sys?.contentType?.sys?.id) {
      // This is a full content entry - preserve structure but process fields
      return {
        id: rawContentModel.sys?.id,
        locale: rawContentModel.sys?.locale,
        type: rawContentModel.sys?.contentType?.sys?.id || rawContentModel.sys?.type,
        ...buildContentModel(rawContentModel.fields || {})
      }
    }
    
    // Check for objects with file fields (could be assets without sys)
    if (rawContentModel.fields?.file?.url) {
      const processedObject = {...rawContentModel}
      processedObject.fields = {
        ...processedObject.fields,
        file: {
          ...processedObject.fields.file,
          url: rewriteUrl(processedObject.fields.file.url)
        }
      }
      
      // Keep sys if it exists
      if (rawContentModel.sys) {
        processedObject.sys = rawContentModel.sys
      }
      return processedObject
    }
    
    // Check for objects with direct file field (graphics without fields wrapper)
    if (rawContentModel.file?.url) {
      const processedObject = {...rawContentModel}
      processedObject.file = {
        ...processedObject.file,
        url: rewriteUrl(processedObject.file.url)
      }
      
      // Keep sys if it exists
      if (rawContentModel.sys) {
        processedObject.sys = rawContentModel.sys
      }
      return processedObject
    }
    
    return Object.entries(rawContentModel).reduce((acc, [key, value]) => {
      if (key === 'sys' || key === 'metadata') return acc
      if (key === 'fields') return { ...buildContentModel(value) }
      acc[key] = buildContentModel(value)
      return acc
    }, {})
  } else {
    return rewriteUrl(rawContentModel)
  }
}

// This function is used to convert the resolved contentful raw_mode response for a program into a format that works with our existing codebase
export function buildProgram(resolvedProgram) {
  const contentModel = buildContentModel(resolvedProgram);
  const program = contentModel[0]
  if (!program) return
  if (!program.level1ProgramUnits || !Array.isArray(program.level1ProgramUnits)) {
    program.level1ProgramUnits = []
    return program
  }
  const level1Units = program.level1ProgramUnits.map((unit) => ({
    ...unit,
    navTitle: unit.title_prefix
      ? `${unit.title_prefix} - ${unit.title}`
      : unit.title,
  }))
  program.level1ProgramUnits = level1Units
  return program
}

// This functions creates a flat object of all program units regardless of level. It allows fast lookup of any program unit by slug and
// it adds references to the previous and next program units in order to facilitate building the navigation for the program
export function buildProgramUnitIndex(program) {
  if (!program) return
  if (!program.level1ProgramUnits || !Array.isArray(program.level1ProgramUnits)) return {}
  const index = {}
  let previousUnit = null

  program.level1ProgramUnits.forEach((unit) => {
    unit = {
      ...unit,
      ...extraProgramUnitFields(program, unit),
    }
    unit.previousProgramUnitSlug = previousUnit?.slug
    if (previousUnit) previousUnit.nextProgramUnitSlug = unit.slug
    previousUnit = unit
    index[unit.slug] = unit
    if (unit.level2ProgramUnits) {
      unit.level2ProgramUnits.forEach((level2Unit) => {
        level2Unit = {
          ...level2Unit,
          ...extraProgramUnitFields(program, unit),
        }
        level2Unit.previousProgramUnitSlug = previousUnit?.slug
        if (previousUnit) previousUnit.nextProgramUnitSlug = level2Unit.slug
        previousUnit = level2Unit
        index[level2Unit.slug] = level2Unit
        if (level2Unit.level3ProgramUnits) {
          level2Unit.level3ProgramUnits.forEach((level3Unit) => {
            level3Unit = {
              ...level3Unit,
              ...extraProgramUnitFields(program, unit),
            }
            level3Unit.previousProgramUnitSlug = previousUnit?.slug
            if (previousUnit) previousUnit.nextProgramUnitSlug = level3Unit.slug
            previousUnit = level3Unit
            index[level3Unit.slug] = level3Unit
          })
        }
      })
    }
  })

  return index
}

function extraProgramUnitFields(program, level1Unit) {
  return {
    programName: program.programName,
    programType: program.programType,
    levelOneTitle: level1Unit.navTitle,
  }
}

// Helper function to convert the raw contentful response to a more usable format
export function resolveContentfulResponse(response, resolveOnly = false) {
  const resolvedResponse = resolveResponse(response)
  if (resolveOnly) return resolvedResponse
  return map(resolvedResponse, (p) => {
    return { id: p.sys.id, locale: p.sys.locale, ...p.fields }
  })
}

// Coverts contentful raw_mode content_blocks (Rich text entries) into a format that works with our existing codebase
export function convertContentfulArrayStructure(arr) {
  if (!arr || !Array.isArray(arr)) return []
  return arr.filter((item) => item != null).map((item) => {
    // First process the entire item through buildContentModel to handle nested assets
    const processedItem = buildContentModel(item)
    return {
      type: 'content_block',
      id: processedItem.id || item.id,
      locale: processedItem.locale || item.locale,
      name: processedItem.name || item.name,
      title: processedItem.title || item.title,
      anchorTag: `content-block-${parameterize(processedItem.name || item.name)}-${processedItem.id || item.id}`,
      defaultClosed: processedItem.defaultClosed || item.defaultClosed,
      bodyContent: convertNestedStructure(processedItem.bodyContent || item.bodyContent),
      tags: [],
      relationshipNames: ['tags'],
    }
  })
}

export function convertNestedStructure(content) {
  // Handle null/undefined content
  if (content == null) {
    return { nodeType: 'document', data: {}, content: [] }
  }
  // Process the entire content structure to rewrite URLs
  const processedContent = buildContentModel(content)
  return {
    nodeType: processedContent?.nodeType || content.nodeType,
    data: processedContent?.data || content.data,
    content: (processedContent?.content || content.content || []).filter((item) => item != null).map((item) => {
      if (
        item.nodeType === 'embedded-entry-block' ||
        item.nodeType === 'embedded-entry-inline' ||
        item.nodeType === 'embedded-asset-block' ||
        item.nodeType === 'embedded-asset-inline'
      ) {
        // Handles nested rich text entries or other special content types
        return serializeNode(item)
      } else {
        // Process each content item to ensure URLs are rewritten
        const processedItem = buildContentModel(item)
        return {
          nodeType: processedItem.nodeType || item.nodeType,
          content: processedItem.content || item.content,
          data: processedItem.data || item.data,
        }
      }
    }),
  }
}

function serializeNode(node) {
  // Process the entire node through buildContentModel
  const processedNode = buildContentModel(node)
  
  // Use the processed target's type as the content type identifier
  const targetType = processedNode.data?.target?.type || node.data?.target?.type
  
  let contentType = getContentType(targetType)
  
  // Special handling for generic "Entry" types - determine actual content type from structure
  if (targetType === 'Entry' && !contentType) {
    const target = processedNode.data?.target
    if (target?.leftColumn && target?.rightColumn) {
      contentType = 'twoColumn'
    } else if (target?.downloadableFile) {
      contentType = 'relatedFile'
    } else if (target?.asset && target?.alignment) {
      contentType = 'image'
    } else if (target?.tableType && target?.tableData) {
      contentType = 'contentTable'
    } else if (target?.link && isRelatedFileResourceType(target?.type)) {
      // RelatedFile entries have a resource type property (Video, Download, etc.) AND a link property.
      // These should render as clickable links, not embedded videos.
      contentType = 'relatedFile'
    } else if (target?.videoUrl) {
      // EmbeddedVideo entries have videoUrl but are NOT RelatedFile entries.
      // If we reach here, it's a true embedded video that should be rendered inline.
      contentType = 'embeddedVideo'
    } else if (target?.markup) {
      contentType = 'iframe'
    } else if (target?.bodyContent) {
      contentType = 'activitiesToStrengthen'
    }
  }
  
  if (!contentType) {
    // if no contentType found, if the node contains data > target > fields > field > file > url the url needs to be rewritten
    const file = processedNode.data?.target?.fields?.file || node.data?.target?.fields?.file
    if (file && file.url) {
      file.url = rewriteUrl(file.url)
    }
    return processedNode
  }
  
  const transformedNode = processedNode
  // Some content types need special handling
  const extraFields = getExtraFields(node, contentType)

  const finalAttributes = {
    locale: transformedNode.data.target.locale,
    ...transformedNode.data.target,
    ...extraFields,
  }

  const serializedNode = {
    nodeType: processedNode.nodeType || node.nodeType,
    content: [],
    data: {
      target: {
        data: {
          id: transformedNode.data.target.id,
          type: contentType,
          attributes: finalAttributes,
        },
      },
    },
  }


  return serializedNode
}

function getExtraFields(node, contentType) {
  const target = node.data?.target
  if (!target) return {}

  switch (contentType) {
    case 'relatedFile': {
      const thumbnailImage = target.thumbnailImage;
      const downloadableFile = target.downloadableFile;

      if(downloadableFile && downloadableFile.raw) {
        downloadableFile.raw = rewriteUrl(downloadableFile.raw)
      }

      if (thumbnailImage && thumbnailImage.raw) {
        thumbnailImage.raw = rewriteUrl(thumbnailImage.raw)
      }

      const file = downloadableFile?.raw || downloadableFile;
      const linkUrl = target.link || file?.fields?.file?.url;

      return {
        type: target.type, // Resource type (Video, Download, etc.) - needed for icon and link text
        title: target.title,
        description: target.description,
        link: linkUrl,
        downloadableFile: file,
        thumbnailImage:
          thumbnailImage?.raw ||
          thumbnailImage,
        anchorTag: `related-file-${parameterize(
          target.title || target.name || 'unknown'
        )}-${target.id}`,
      }
    }
    case 'twoColumn':
      return {
        leftColumn: convertNestedStructure(target.leftColumn),
        rightColumn: convertNestedStructure(target.rightColumn),
      }
    case 'activitiesToStrengthen':
      return {
        bodyContent: convertNestedStructure(target.bodyContent),
      }
    case 'image': {
      const asset = target.asset;
      
      // Ensure the asset URL is rewritten if it exists
      if (asset && asset.fields && asset.fields.file && asset.fields.file.url) {
        asset.fields.file.url = rewriteUrl(asset.fields.file.url);
      }
      
      return {
        asset: asset,
        alignment: target.alignment,
        imageSize: target.imageSize,
        anchorTag: `image-${parameterize(
          target.name || asset?.fields?.title || 'unknown'
        )}-${target.id}`,
      }
    }
    case 'contentTable': {
      return {
        tableType: target.tableType,
        tableData: target.tableData,
        anchorTag: `table-${parameterize(
          target.name || target.title || 'unknown'
        )}-${target.id}`,
      }
    }
    case 'embeddedVideo': {
      // Handle both videoUrl and link properties
      const videoUrl = target.videoUrl || target.link
      return {
        videoUrl: videoUrl,
        anchorTag: `video-${parameterize(
          target.name || target.title || 'unknown'
        )}-${target.id}`,
      }
    }
    case 'iframe': {
      return {
        markup: target.markup,
        anchorTag: `iframe-${parameterize(
          target.name || target.title || 'unknown'
        )}-${target.id}`,
      }
    }
    default:
      return {}
  }
}

function getContentType(contentTypeId) {
  if (!contentTypeId) return
  switch (contentTypeId) {
    case 'relatedFiles':
      return 'relatedFile'
    case 'Download':
      return 'relatedFile'
    case 'iFrame':
      return 'iframe'
    case 'table':
    case 'Table':
      return 'contentTable'
    // Note: 'Video' is a resource TYPE for RelatedFile entries (rendered as links),
    // NOT a Contentful content type. The actual Contentful content type for
    // inline videos is 'embeddedVideo' which is handled by the default camelCase case.
    case 'Entry':
      // For generic "Entry" types, we need to look at the structure
      return null // Let the caller handle this case
    default:
      return camelCase(contentTypeId)
  }
}

function parameterize(str) {
  return slugify(toLower(str), { strict: true })
}

/**
 * Checks if a type value is a RelatedFile resource type (Video, Download, etc.)
 * These are distinct from Contentful sys.type values like "Entry" or "Asset"
 */
function isRelatedFileResourceType(type: string | undefined): boolean {
  if (!type) return false
  const relatedFileResourceTypes = ['Video', 'Download', 'Link to Proxima', 'Link to Research Hub', 'Link to Instructor Hub', 'Link to Knowledge Hub', 'Modal']
  return relatedFileResourceTypes.includes(type)
}

/**
 * Rewrites Contentful asset URLs to use session-authenticated API redirect.
 * The API endpoint signs URLs with Contentful's 40-hour signed URL system.
 *
 * Input: //images.ctfassets.net/{space}/{asset}/{filename}?w=800
 * Output: {API_URL}/api/assets/{space}/{asset}/{filename}?subdomain=images&w=800
 */
export function rewriteUrl(url: string): string {
  if (!url || typeof url !== 'string') return url || '';

  // Skip non-URL strings (e.g., plain text like "Knowledge Hub")
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('//')) {
    return url;
  }

  try {
    const parsedUrl = new URL(url.startsWith('//') ? `https:${url}` : url);

    // Check if this is a Contentful asset URL
    if (!parsedUrl.hostname || !CONTENTFUL_ASSETS_HOSTNAMES.includes(parsedUrl.hostname)) {
      return url;
    }

    // Already rewritten to API endpoint - return as-is
    if (url.includes('/api/assets/')) {
      return url;
    }

    // Extract: images.ctfassets.net/{space}/{asset}/{filename}
    const subdomain = parsedUrl.hostname.split('.')[0]; // 'images', 'assets', 'videos', etc.
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
    const [spaceId, assetId, ...rest] = pathParts;

    if (!spaceId || !assetId) return url;

    // Build API URL: /api/assets/{space}/{asset}/{path}?subdomain=images&...
    const apiBase = import.meta.env.VITE_PROSCI_CONNECT_API_URL || '';
    const assetPath = rest.length > 0 ? rest.join('/') : '';
    const apiUrl = new URL(`${apiBase}/api/assets/${spaceId}/${assetId}/${assetPath}`);

    // Add subdomain as query param for backend to reconstruct correct host
    apiUrl.searchParams.set('subdomain', subdomain);

    // Preserve original query params (Contentful image transforms: w, h, fit, fm, q, fl, etc.)
    parsedUrl.searchParams.forEach((value, key) => {
      apiUrl.searchParams.set(key, value);
    });

    return apiUrl.toString();
  } catch (error) {
    console.error('[rewriteUrl] Error:', error);
    return url;
  }
}

